import { Report, ReportFilter } from '@idea2app/ai-work-order-service';
import { Icon } from 'idea-react';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Column, Field, RestTable } from 'mobx-restful-table';
import { Component, FormEvent } from 'react';
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Modal,
    Row
} from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-class-tools';
import { formToJSON } from 'web-utility';

import { ReportCreationForm } from '../component/ReportCreationForm';
import { StatisticCard } from '../component/StatisticCard';
import reportStore, {
    ReportStatusColors,
    ReportStatusLabels,
    ReportTypeLabels
} from '../model/Report';
import { i18n, I18nKey, t } from '../model/Translation';

@withRouter
@observer
export class ReportListPage extends Component<RouteComponentProps> {
    @observable
    accessor showCreateModal = false;

    @computed
    get filterFields(): Field<ReportFilter>[] {
        return [
            { key: 'keywords', renderLabel: t('search') },
            { key: 'name', renderLabel: t('report_name') },
            {
                key: 'reportType',
                type: 'select',
                renderLabel: t('report_type'),
                options: [
                    { value: '', label: t('all_statuses') },
                    { value: 'weekly', label: t('report_type_weekly') },
                    { value: 'monthly', label: t('report_type_monthly') },
                    { value: 'quarterly', label: t('report_type_quarterly') },
                    { value: 'annual', label: t('report_type_yearly') }
                ]
            },
            {
                key: 'status',
                type: 'select',
                renderLabel: t('report_status'),
                options: [
                    { value: '', label: t('all_statuses') },
                    { value: 'in_progress', label: t('report_in_progress') },
                    { value: 'queued', label: t('report_queued') },
                    { value: 'completed', label: t('report_completed') }
                ]
            }
        ];
    }

    @computed
    get columns(): Column<Report>[] {
        return [
            {
                key: 'name',
                renderHead: t('report_name'),
                renderBody: ({ name }) => (
                    <div className="d-flex align-items-center">
                        <Icon
                            name="file-earmark-text"
                            className="me-2 text-primary"
                        />
                        <span>{name}</span>
                    </div>
                )
            },
            {
                key: 'reportType',
                renderHead: t('report_type'),
                renderBody: ({ reportType }) =>
                    t(ReportTypeLabels[reportType] as I18nKey)
            },
            {
                renderHead: t('analysis_range'),
                renderBody: ({ startDate, endDate }) => (
                    <span>
                        {new Date(startDate).toLocaleDateString()} ~{' '}
                        {new Date(endDate).toLocaleDateString()}
                    </span>
                )
            },
            {
                key: 'createdAt',
                renderHead: t('created_at'),
                renderBody: ({ createdAt }) =>
                    new Date(createdAt).toLocaleString()
            },
            {
                key: 'status',
                renderHead: t('report_status'),
                renderBody: ({ status }) => (
                    <Badge bg={ReportStatusColors[status]}>
                        {t(ReportStatusLabels[status] as I18nKey)}
                    </Badge>
                )
            },
            {
                renderHead: t('operation'),
                // @ts-expect-error Wait for Back-end updated
                renderBody: ({ status, fileLink }) => (
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="link" size="sm">
                            <Icon name="eye" className="me-1" />
                            {t('preview')}
                        </Button>
                        {status === 'completed' && fileLink && (
                            <Button
                                variant="link"
                                size="sm"
                                className="text-success"
                            >
                                <Icon name="download" className="me-1" />
                                {t('download')}
                            </Button>
                        )}
                    </div>
                )
            }
        ];
    }

    handleCreateReport = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        await reportStore.updateOne(formToJSON(event.currentTarget));
        // This would normally create the report via API
        this.showCreateModal = false;
        // Reset form
    };

    render() {
        return (
            <Container className="py-4" fluid>
                {/* Statistics Cards */}
                <Row className="mb-4 g-3">
                    <Col md={3}>
                        <StatisticCard
                            title={t('monthly_total_orders')}
                            value="1,234"
                            icon="clipboard-minus-fill"
                            color="primary"
                            change="15.3%"
                            changeType="increase"
                        />
                    </Col>
                    <Col md={3}>
                        <StatisticCard
                            title={t('pending_orders')}
                            value="45"
                            icon="clock-fill"
                            color="warning"
                            change="8.2%"
                            changeType="decrease"
                        />
                    </Col>
                    <Col md={3}>
                        <StatisticCard
                            title={t('processing_timeliness')}
                            value="98.5%"
                            icon="check-circle-fill"
                            color="success"
                            change="2.1%"
                            changeType="increase"
                        />
                    </Col>
                    <Col md={3}>
                        <StatisticCard
                            title={t('abnormal_orders')}
                            value="23"
                            icon="exclamation-triangle-fill"
                            color="danger"
                            change="12.4%"
                            changeType="decrease"
                        />
                    </Col>
                </Row>

                {/* Report List */}
                <Card>
                    <Card.Body>
                        <div className="mb-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <Icon name="list-task" className="me-2" />
                                {t('report_list')}
                            </h5>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    onClick={() =>
                                        (this.showCreateModal = true)
                                    }
                                >
                                    <Icon name="plus-circle" className="me-2" />
                                    {t('create_report_task')}
                                </Button>
                            </div>
                        </div>
                        <RestTable
                            className="text-center"
                            filterFields={this.filterFields}
                            columns={this.columns}
                            store={reportStore}
                            translator={i18n}
                            striped
                            hover
                        />
                    </Card.Body>
                </Card>

                {/* Create Report Modal */}
                <Modal
                    show={this.showCreateModal}
                    size="lg"
                    onHide={() => (this.showCreateModal = false)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{t('create_report_task')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <ReportCreationForm
                            onSubmit={this.handleCreateReport}
                            onReset={() => (this.showCreateModal = false)}
                        />
                    </Modal.Body>
                </Modal>
            </Container>
        );
    }
}
